// src/contexts/UserRoleContext.js — VERSION STABLE + REGISTRE D’ÉCOUTEURS
// Hiérarchie: Propriétaire (SuperAdmin) > Admin > Docteur > Vendeuse/Assistant
// Logique inversée conservée pour vendeuse: accès large sauf retraits explicites.
// Ajouts majeurs: watchRegistry (unique listener par ressource) + attachUniqueSnapshot()

import React, {
  createContext,
  useContext,
  useState,
  useEffect,
  useRef,
  useMemo,
} from "react";
import { getAuth, onAuthStateChanged, signOut } from "firebase/auth";
import "../firebase/app-auth"; // initialise Firebase App + Auth sans Firestore
import logger from "../utils/logger";
import permissions, {
  DOCTOR_ONLY_PERMISSIONS,
  normalizePermissions,
} from "../utils/permissions";

// ── Firestore chargé de façon différée (après authentification) ──
let _db = null;
let _doc = null;
let _getDoc = null;
let _onSnapshot = null;
let _updateDoc = null;
let _disableFirestoreNetwork = null;
let _enableFirestoreNetwork = null;
let _firestoreReady = null;

async function ensureFirestore() {
  if (_db) return;
  if (!_firestoreReady) {
    _firestoreReady = Promise.all([
      import('firebase/firestore'),
      import('../firebase/config'),
    ]).then(([fs, cfg]) => {
      _db = cfg.db;
      _doc = fs.doc;
      _getDoc = fs.getDoc;
      _onSnapshot = fs.onSnapshot;
      _updateDoc = fs.updateDoc;
      _disableFirestoreNetwork = cfg.disableFirestoreNetwork;
      _enableFirestoreNetwork = cfg.enableFirestoreNetwork;
    });
  }
  return _firestoreReady;
}

const UserRoleContext = createContext();

/** Ferme tous les listeners Firestore — à appeler avant signOut() */
export function detachAllListeners() { detachAll(); }

export function useUserRole() {
  const ctx = useContext(UserRoleContext);
  if (!ctx) throw new Error("useUserRole doit être utilisé dans un UserRoleProvider");
  return ctx;
}

/**
 * Hook optimisé pour vérifier une seule permission.
 * Évite le re-render du composant si d'autres valeurs du contexte changent.
 * Usage : const peutVendre = usePermission("ventes");
 */
export function usePermission(permissionName) {
  const { can, role, removedPermissions } = useContext(UserRoleContext) || {};
  return useMemo(
    () => (can ? can(permissionName) : false),
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [permissionName, role, removedPermissions]
  );
}

/**
 * Hook léger pour les composants qui n'ont besoin que de l'état d'authentification.
 * N'entraîne pas de re-render lors des changements de permissions ou de soldes.
 */
export function useAuthState() {
  const { user, loading, authReady, societeId, societeName, isOfflineSession } =
    useContext(UserRoleContext) || {};
  return useMemo(
    () => ({ user, loading, authReady, societeId, societeName, isOfflineSession }),
    [user, loading, authReady, societeId, societeName, isOfflineSession]
  );
}

/* =========================================================
   🧷 Registre module-scopé des écouteurs Firestore
   - Un seul onSnapshot par clé (ex: "users/{uid}", "societe/{sid}")
   - Toute nouvelle attache ferme l’ancienne proprement
========================================================= */
const watchRegistry = new Map();
/**
 * @param {string} key   ex: `users/${uid}`
 * @param {import('firebase/firestore').DocumentReference} ref
 * @param {(snap)=>void} onOk
 * @param {(err)=>void} onErr
 */
function attachUniqueSnapshot(key, ref, onOk, onErr) {
  // Ferme l’ancien listener s’il existe
  try {
    const prev = watchRegistry.get(key);
    if (typeof prev === "function") {
      prev();
    }
  } catch {
    /* ignore */
  }
  // Attache le nouveau et mémorise l’unsub
  const unsub = _onSnapshot(ref, onOk, onErr);
  watchRegistry.set(key, unsub);
  return () => {
    const cur = watchRegistry.get(key);
    if (cur === unsub) {
      try { cur(); } catch {}
      watchRegistry.delete(key);
    } else {
      // Un autre s’est déjà enregistré -> on laisse celui en place
      try { unsub(); } catch {}
    }
  };
}

/** Ferme et oublie une clé précisément (utile en cleanup ciblé) */
function detachKey(key) {
  const unsub = watchRegistry.get(key);
  if (typeof unsub === "function") {
    try { unsub(); } catch {}
  }
  watchRegistry.delete(key);
}

/** Tout fermer (au démontage provider) */
function detachAll() {
  for (const [, unsub] of watchRegistry) {
    try { typeof unsub === "function" && unsub(); } catch {}
  }
  watchRegistry.clear();
}

export function UserRoleProvider({ children }) {
  // ---------- États principaux ----------
  const [user, setUser] = useState(null);
  const [role, setRole] = useState(null);
  const [societeId, setSocieteId] = useState(null);
  const [societeName, setSocieteName] = useState(null);

  // ---------- États de statut ----------
  const [authReady, setAuthReady] = useState(false);
  const [loading, setLoading] = useState(true);
  const [isOwner, setIsOwner] = useState(false);
  // isOfflineSession = true quand la session vient du cache localStorage (pas Firebase Auth)
  const [isOfflineSession, setIsOfflineSession] = useState(false);
  const [isActive, setIsActive] = useState(true);
  const [isLocked, setIsLocked] = useState(false);
  const [isDeleted, setIsDeleted] = useState(false);

  // ---------- Verrouillage de la société (par l'admin global) ----------
  const [isSocieteLocked, setIsSocieteLocked] = useState(false);

  // ---------- Solde & Avances (mis à jour par l'admin) ----------
  const [montantDu, setMontantDu]       = useState(0);
  const [totalAvances, setTotalAvances] = useState(0);
  const [soldeDu, setSoldeDu]           = useState(0);

  // ---------- Notifications ----------
  const [adminPopup, setAdminPopup] = useState(null);
  const [paymentWarning, setPaymentWarning] = useState(null);

  // ---------- Problème de session détecté (auto-recovery épuisé) ----------
  const [sessionError, setSessionError] = useState(false);

  // ---------- Permissions retirées (logique inversée) ----------
  const [removedPermissions, setRemovedPermissions] = useState([]);

  // ---------- Refs de suivi ----------
  const authUnsubRef = useRef(null);
  const currentUserIdRef = useRef(null);
  const currentSocieteIdRef = useRef(null);
  const currentUserRef = useRef(null);
  const currentRoleRef = useRef(null);
  const lastUserKeyRef = useRef(null);
  const lastSocKeyRef = useRef(null);
  // Identifiant de session : incrémenté à chaque login pour invalider les callbacks des sessions précédentes
  const sessionIdRef = useRef(0);
  // Timeout de chargement courant : doit être annulé à chaque déconnexion
  const pendingTimeoutRef = useRef(null);
  // Délai de grâce avant déconnexion réelle (évite le flash logout lors du refresh de token Firebase)
  const nullLogoutTimerRef = useRef(null);
  // Compteur de tentatives auto-recovery (reset à chaque connexion réussie)
  const recoveryAttemptsRef = useRef(0);
  // Verrou pour éviter des refreshSession concurrents
  const refreshingRef = useRef(false);
  // Ref vers refreshSession (évite les dépendances circulaires dans les closures)
  const refreshSessionRef = useRef(null);
  // Mode hors ligne : actif quand l'utilisateur a repris une session depuis le cache localStorage
  const offlineModeRef = useRef(false);

  const safeLower = (s) => (typeof s === "string" ? s.toLowerCase() : "");

  const tryRecoverWatchError = async (err) => {
    const msg = String(err?.message || "");
    if (msg.includes("Target ID already exists")) {
      try {
        if (_disableFirestoreNetwork) await _disableFirestoreNetwork();
        await new Promise((r) => setTimeout(r, 150));
        if (_enableFirestoreNetwork) await _enableFirestoreNetwork();
      } catch {}
    }
  };

  // =========================
  // Écoute de l'auth Firebase
  // =========================
  useEffect(() => {
    const auth = getAuth();

    // Cleanup auth si existe
    if (authUnsubRef.current) {
      authUnsubRef.current();
      authUnsubRef.current = null;
    }

    authUnsubRef.current = onAuthStateChanged(auth, async (firebaseUser) => {
      setAuthReady(true);

      // Déconnexion → vérifier d'abord si une session offline doit être restaurée
      if (!firebaseUser) {
        // Session offline persistée dans sessionStorage (ex: après window.location.reload)
        if (!offlineModeRef.current) {
          try {
            const offlineUid = sessionStorage.getItem('pharma_offline_uid');
            if (offlineUid) {
              const cached = JSON.parse(localStorage.getItem(`pharma_profile_${offlineUid}`) || 'null');
              if (cached?.societeId) {
                offlineModeRef.current = true;
                setIsOfflineSession(true);
                currentUserRef.current = cached;
                const norm = normalizePermissions(cached.removedPermissions || []);
                setUser(cached);
                setRole(cached.role);
                setSocieteId(cached.societeId || null);
                setIsOwner(!!cached.isOwner);
                setIsActive(cached.active !== false);
                setIsLocked(!!cached.locked);
                setIsDeleted(!!cached.deleted);
                setAdminPopup(cached.adminPopup || null);
                setPaymentWarning(cached.paymentWarning || null);
                setRemovedPermissions(norm);
                setLoading(false);
                return; // session offline restaurée → ne pas réinitialiser
              }
            }
          } catch {}
        }
        // Délai de grâce : Firebase peut brièvement passer null lors du refresh du token (toutes les ~1h).
        // On attend 2500ms (au lieu de 500ms) pour couvrir les refreshs de token lents en production.
        if (nullLogoutTimerRef.current) clearTimeout(nullLogoutTimerRef.current);
        nullLogoutTimerRef.current = setTimeout(() => {
          nullLogoutTimerRef.current = null;

          // Mode hors ligne actif → ne pas réinitialiser la session
          // (ne pas utiliser navigator.onLine : il peut retourner true sans internet réel)
          if (offlineModeRef.current) return;

          // ── VÉRIFICATION CRITIQUE ──
          // Avant de tout réinitialiser, vérifier si Firebase a déjà un utilisateur connecté.
          // Si auth.currentUser est non-null, c'est un refresh de token (pas un vrai logout) :
          // la session vient d'être renouvelée mais onAuthStateChanged(user) n'a pas encore refired.
          // → Ne pas réinitialiser, juste re-attacher le listener Firestore si le profil manque.
          const authInst = getAuth();
          if (authInst.currentUser) {
            if (!currentUserRef.current) {
              // Profil manquant après refresh → forcer re-init sans perdre la session
              currentUserIdRef.current = null;
              refreshingRef.current = false;
              // Déclencher onAuthStateChanged manuellement via getIdToken
              authInst.currentUser.getIdToken(true).catch(() => {});
            }
            return; // Ne pas réinitialiser : l'utilisateur est toujours là
          }

          // Vrai logout → effacer le flag session offline
          try { sessionStorage.removeItem('pharma_offline_uid'); } catch {}

          // Invalider la session courante pour bloquer tout callback asynchrone encore en vol
          sessionIdRef.current += 1;

          // Annuler le timeout de chargement s'il est encore actif
          if (pendingTimeoutRef.current) {
            clearTimeout(pendingTimeoutRef.current);
            pendingTimeoutRef.current = null;
          }

          // Fermer TOUS les listeners Firestore (plus sûr que detachKey individuel)
          detachAll();
          lastUserKeyRef.current = null;
          lastSocKeyRef.current = null;

          currentUserIdRef.current = null;
          currentSocieteIdRef.current = null;
          currentRoleRef.current = null;
          currentUserRef.current = null;

          setUser(null);
          setRole(null);
          setSocieteId(null);
          setSocieteName(null);
          setIsOwner(false);
          setIsActive(true);
          setIsLocked(false);
          setIsDeleted(false);
          setRemovedPermissions([]);
          setAdminPopup(null);
          setPaymentWarning(null);
          setIsSocieteLocked(false);
          setMontantDu(0); setTotalAvances(0); setSoldeDu(0);
          setLoading(false);
        }, 2500);
        return;
      }

      // Un utilisateur est présent → annuler tout timer de logout en attente
      if (nullLogoutTimerRef.current) {
        clearTimeout(nullLogoutTimerRef.current);
        nullLogoutTimerRef.current = null;
      }

      // Sortir du mode hors ligne si on en était (Firebase a maintenant un utilisateur valide)
      offlineModeRef.current = false;
      setIsOfflineSession(false);
      try { sessionStorage.removeItem('pharma_offline_uid'); } catch {}

      // Nouvelle connexion (UID différent) → reset du compteur d'auto-recovery
      // IMPORTANT : ne pas reset si même UID + societeId déjà confirmé null dans Firestore
      // car ça relancerait une boucle infinie à chaque refresh de token Firebase (toutes les heures)
      if (currentUserIdRef.current !== firebaseUser.uid) {
        recoveryAttemptsRef.current = 0;
      }

      // Même UID + profil déjà chargé → rien à ré-attacher
      // IMPORTANT : si currentUserRef est null (profil non encore reçu), ne pas court-circuiter
      // car le listener Firestore a peut-être été perdu (réveil veille, reconnexion réseau…)
      if (currentUserIdRef.current === firebaseUser.uid && currentUserRef.current) {
        setLoading(false);
        return;
      }

      // Détacher ancien user + soc
      if (lastUserKeyRef.current) {
        detachKey(lastUserKeyRef.current);
        lastUserKeyRef.current = null;
      }
      if (lastSocKeyRef.current) {
        detachKey(lastSocKeyRef.current);
        lastSocKeyRef.current = null;
      }

      currentUserIdRef.current = firebaseUser.uid;

      // Capturer l'ID de session AVANT toute opération async
      // Tout callback qui arrive avec un sessionId obsolète sera ignoré
      const mySessionId = ++sessionIdRef.current;

      // Marquer comme en chargement pendant que le snapshot Firestore s'établit
      // (évite que Protected redirige vers /login avant que le profil soit chargé)
      setLoading(true);

      // Charger Firestore (lazy) — uniquement après authentification
      await ensureFirestore();
      if (sessionIdRef.current !== mySessionId) return; // session invalidée pendant le chargement

      // Attacher listener unique sur users/{uid}
      try {
        const userRef = _doc(_db, "users", firebaseUser.uid);
        const userKey = `users/${firebaseUser.uid}`;

        lastUserKeyRef.current = userKey;

        // Clé localStorage pour le profil hors ligne
        const lsKey = `pharma_profile_${firebaseUser.uid}`;

        // Timeout de sécurité : si le snapshot ne répond pas en 6s (hors ligne + cache vide),
        // on utilise le profil mis en cache dans localStorage pour ne pas bloquer l'app.
        if (pendingTimeoutRef.current) {
          clearTimeout(pendingTimeoutRef.current);
          pendingTimeoutRef.current = null;
        }
        pendingTimeoutRef.current = setTimeout(() => {
          // Session invalidée (logout ou re-login entre-temps) → ne rien faire
          if (sessionIdRef.current !== mySessionId) return;
          pendingTimeoutRef.current = null;
          if (!currentUserRef.current) {
            try {
              const cached = JSON.parse(localStorage.getItem(lsKey) || "null");
              if (cached?.societeId) {
                logger.warn("[UserRoleContext] Snapshot timeout — utilisation du profil localStorage");
                currentUserRef.current = cached;
                currentRoleRef.current = cached.role;
                setUser(cached);
                setRole(cached.role);
                setSocieteId(cached.societeId);
                setIsOwner(!!cached.isOwner);
                setIsActive(cached.active !== false);
                setIsLocked(!!cached.locked);
                setIsDeleted(!!cached.deleted);
                setRemovedPermissions(normalizePermissions(cached.removedPermissions || []));
              }
            } catch {}
            setLoading(false);
          }
        }, 6000);

        attachUniqueSnapshot(
          userKey,
          userRef,
          (snap) => {
            // Session invalidée → ignorer ce callback
            if (sessionIdRef.current !== mySessionId) return;
            if (pendingTimeoutRef.current) {
              clearTimeout(pendingTimeoutRef.current);
              pendingTimeoutRef.current = null;
            }
            let base;
            if (snap.exists()) {
              const data = snap.data() || {};

              // ── Réinitialisation de session forcée par l'admin SaaS ──
              if (data.sessionReset === true) {
                (async () => {
                  try {
                    await _updateDoc(_doc(_db, "users", firebaseUser.uid), { sessionReset: false });
                  } catch (_) {}
                  try {
                    Object.keys(localStorage)
                      .filter(k => k.startsWith("pharma_"))
                      .forEach(k => localStorage.removeItem(k));
                    sessionStorage.clear();
                  } catch (_) {}
                  await signOut(getAuth()).catch(() => {});
                })();
                return;
              }

              const userRole = safeLower(data.role || "vendeuse");
              const normalizedRemoved = normalizePermissions(data.removedPermissions || []);

              base = {
                uid: firebaseUser.uid,
                email: firebaseUser.email || "",
                displayName: data.displayName || data.nom || firebaseUser.displayName || "",
                photoURL: data.photoURL || firebaseUser.photoURL || "",
                nom: data.nom || data.displayName || firebaseUser.displayName || "",
                role: userRole,
                societeId: data.societeId || null,
                isOwner: data.isOwner === true,
                active: data.active !== false && data.isActive !== false,
                locked: data.locked === true || data.isLocked === true,
                deleted: data.deleted === true,
                adminPopup: data.adminPopup || null,
                paymentWarning: data.paymentWarning || null,
                removedPermissions: normalizedRemoved,
              };

              // Mettre en cache pour fonctionnement hors ligne futur
              if (base.societeId) {
                try {
                  localStorage.setItem(lsKey, JSON.stringify(base));
                  localStorage.setItem('pharma_last_uid', base.uid);
                } catch {}
              }
            } else {
              // Pas de doc user en cache Firestore → essayer localStorage
              try {
                const cached = JSON.parse(localStorage.getItem(lsKey) || "null");
                if (cached?.societeId) {
                  base = cached;
                }
              } catch {}

              if (!base) {
                base = {
                  uid: firebaseUser.uid,
                  email: firebaseUser.email || "",
                  displayName: firebaseUser.displayName || "",
                  role: "docteur",
                  societeId: null,
                  isOwner: false,
                  active: true,
                  locked: false,
                  deleted: false,
                  adminPopup: null,
                  paymentWarning: null,
                  removedPermissions: [],
                };
              }
            }

            currentUserRef.current = base;
            currentRoleRef.current = base.role;

            setUser(base);
            setRole(base.role);
            setSocieteId(base.societeId);
            setIsOwner(!!base.isOwner);
            setIsActive(base.active !== false);
            setIsLocked(!!base.locked);
            setIsDeleted(!!base.deleted);
            setAdminPopup(base.adminPopup || null);
            setPaymentWarning(base.paymentWarning || null);
            setRemovedPermissions(normalizePermissions(base.removedPermissions || []));
            setLoading(false);
          },
          async (error) => {
            if (sessionIdRef.current !== mySessionId) return;
            if (pendingTimeoutRef.current) {
              clearTimeout(pendingTimeoutRef.current);
              pendingTimeoutRef.current = null;
            }

            const msg = String(error?.message || "");
            const isPermError = msg.includes("Missing or insufficient permissions") ||
                                msg.includes("PERMISSION_DENIED");

            if (isPermError) {
              const auth = getAuth();
              // Utilisateur déconnecté (signOut) → ignorer silencieusement
              if (!auth.currentUser) return;
              // Token expiré → forcer refresh du token, attendre sa propagation
              // puis ré-attacher le listener via refreshSession
              try {
                await auth.currentUser.getIdToken(true);
                // Délai de 1.5s : laisse le SDK Firestore utiliser le nouveau token
                // avant de créer un nouvel onSnapshot (évite permission-denied en cascade)
                await new Promise(r => setTimeout(r, 1500));
              } catch (_refreshErr) { /* ignore, on tente quand même refreshSession */ }
              if (!auth.currentUser) return; // vérifier à nouveau après l'attente
              currentUserIdRef.current = null;
              lastUserKeyRef.current = null;
              detachKey(userKey);
              refreshSessionRef.current?.();
              return;
            } else {
              logger.error("[UserRoleContext] Erreur listener user:", error);
            }

            // Fallback localStorage en cas d'erreur non récupérable
            let fb;
            try {
              const cached = JSON.parse(localStorage.getItem(lsKey) || "null");
              if (cached?.societeId) fb = cached;
            } catch {}

            if (!fb) {
              fb = {
                uid: firebaseUser.uid,
                email: firebaseUser.email || "",
                displayName: firebaseUser.displayName || "",
                role: "docteur",
                societeId: null,
                isOwner: false,
                active: true,
                locked: false,
                deleted: false,
                adminPopup: null,
                paymentWarning: null,
                removedPermissions: [],
              };
            }

            currentUserRef.current = fb;
            currentRoleRef.current = fb.role;

            setUser(fb);
            setRole(fb.role);
            setSocieteId(fb.societeId);
            setIsOwner(!!fb.isOwner);
            setIsActive(fb.active !== false);
            setIsLocked(!!fb.locked);
            setIsDeleted(!!fb.deleted);
            setRemovedPermissions(normalizePermissions(fb.removedPermissions || []));
            setAdminPopup(null);
            setPaymentWarning(null);
            setLoading(false);

            await tryRecoverWatchError(error);
          }
        );
      } catch (e) {
        if (sessionIdRef.current !== mySessionId) return;
        logger.error("[UserRoleContext] Erreur attachement listener user:", e);

        // Fallback localStorage
        let fb;
        try {
          const cached = JSON.parse(localStorage.getItem(`pharma_profile_${firebaseUser.uid}`) || "null");
          if (cached?.societeId) fb = cached;
        } catch {}

        if (!fb) {
          fb = {
            uid: firebaseUser.uid,
            email: firebaseUser.email || "",
            displayName: firebaseUser.displayName || "",
            role: "docteur",
            societeId: null,
            isOwner: false,
            active: true,
            locked: false,
            deleted: false,
            adminPopup: null,
            paymentWarning: null,
            removedPermissions: [],
          };
        }
        currentUserRef.current = fb;
        currentRoleRef.current = fb.role;

        setUser(fb);
        setRole(fb.role);
        setSocieteId(fb.societeId);
        setIsOwner(!!fb.isOwner);
        setIsActive(fb.active !== false);
        setIsLocked(!!fb.locked);
        setIsDeleted(!!fb.deleted);
        setRemovedPermissions(normalizePermissions(fb.removedPermissions || []));
        setAdminPopup(null);
        setPaymentWarning(null);
        setLoading(false);

        await tryRecoverWatchError(e);
      }
    });

    return () => {
      if (authUnsubRef.current) {
        authUnsubRef.current();
        authUnsubRef.current = null;
      }
      // Annuler le timer de grâce s'il est en attente
      if (nullLogoutTimerRef.current) {
        clearTimeout(nullLogoutTimerRef.current);
        nullLogoutTimerRef.current = null;
      }
      // Fermer tout si le provider se démonte
      detachAll();
    };
  }, []);

  // =========================
  // Récupération automatique de session
  // Déclenché quand l'onglet redevient visible ou quand le réseau revient
  // Evite la perte de session après mise en veille ou coupure réseau
  // =========================
  useEffect(() => {
    const recover = () => {
      const authInst = getAuth();
      const cu = authInst.currentUser;
      // Si Firebase dit "connecté" mais qu'on n'a plus le profil → forcer re-init via refreshSession
      if (cu && !currentUserRef.current && !refreshingRef.current) {
        refreshSession();
      }
    };

    const onVisible = () => {
      if (document.visibilityState === "visible") recover();
    };
    const onOnline = () => recover();

    document.addEventListener("visibilitychange", onVisible);
    window.addEventListener("online", onOnline);
    return () => {
      document.removeEventListener("visibilitychange", onVisible);
      window.removeEventListener("online", onOnline);
    };
  }, []);

  // =========================
  // Écoute du document société
  // =========================
  useEffect(() => {
    if (!user || !societeId || isDeleted) {
      if (lastSocKeyRef.current) {
        detachKey(lastSocKeyRef.current);
        lastSocKeyRef.current = null;
      }
      setSocieteName(null);
      currentSocieteIdRef.current = null;
      return;
    }

    // Forcer le re-attach si la société est la même mais l’UID a changé (re-login même société)
    // On vérifie la combinaison uid+societeId pour éviter le court-circuit sur re-login
    const sessionKey = `${user.uid}__${societeId}`;
    if (currentSocieteIdRef.current === sessionKey) return;

    // Détache l’ancien
    if (lastSocKeyRef.current) {
      detachKey(lastSocKeyRef.current);
      lastSocKeyRef.current = null;
    }
    currentSocieteIdRef.current = sessionKey;

    const mySessionId = sessionIdRef.current;

    try {
      const ref = _doc(_db, "societe", societeId);
      const socKey = `societe/${societeId}`;
      lastSocKeyRef.current = socKey;

      attachUniqueSnapshot(
        socKey,
        ref,
        (snap) => {
          if (sessionIdRef.current !== mySessionId) return;
          if (snap.exists()) {
            const data = snap.data();
            setSocieteName(data?.nom || data?.name || "Pharmacie");
            setIsSocieteLocked(data?.active === false || data?.status === "locked");
            setMontantDu(Number(data?.montantDu) || 0);
            setTotalAvances(Number(data?.totalAvances) || 0);
            setSoldeDu(Number(data?.soldeDu) || 0);
          } else {
            setSocieteName("Pharmacie");
            setIsSocieteLocked(false);
            setMontantDu(0); setTotalAvances(0); setSoldeDu(0);
          }
        },
        async (err) => {
          if (sessionIdRef.current !== mySessionId) return;
          const emsg = String(err?.message || "");
          const isPermErr = emsg.includes("Missing or insufficient permissions") || emsg.includes("PERMISSION_DENIED");
          // Déconnexion en cours → ignorer silencieusement
          if (isPermErr && !getAuth().currentUser) return;
          logger.warn("[UserRoleContext] Erreur chargement société:", err);
          setSocieteName("Pharmacie");
          await tryRecoverWatchError(err);
        }
      );
    } catch (e) {
      if (sessionIdRef.current === mySessionId) {
        logger.warn("[UserRoleContext] Erreur attachement société:", e);
        setSocieteName("Pharmacie");
      }
    }

    return () => {
      if (lastSocKeyRef.current) {
        detachKey(lastSocKeyRef.current);
        lastSocKeyRef.current = null;
      }
      currentSocieteIdRef.current = null;
    };
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [user?.uid, societeId, isDeleted]);

  // =========================
  // Permissions (logique inversée)
  // =========================
  const effectivePermissions = useMemo(() => {
    const r = safeLower(role);
    if (isOwner || r === "docteur" || r === "admin") {
      return permissions.docteur || [];
    }
    const all = (permissions.docteur || []).filter(
      (p) => !DOCTOR_ONLY_PERMISSIONS.includes(p)
    );
    if (!removedPermissions?.length) return all;
    return all.filter((p) => !removedPermissions.includes(p));
  }, [role, isOwner, removedPermissions]);

  // ---------- Helpers d'accès ----------
  const canAccessApp = () => {
    if (!authReady) return false;
    if (!user) return false;
    if (isDeleted) return false;
    if (isLocked && !isOwner) return false;
    if (!isActive && !isOwner) return false;
    return true;
  };

  const isAdmin = () => {
    const r = safeLower(role);
    return (r === "admin" || r === "docteur" || isOwner) && canAccessApp();
  };
  const isSuperAdmin = () => !!isOwner && canAccessApp();

  const canManageUsers = () => isAdmin();
  const canChangeRoles = () => isAdmin();
  const canDeleteSociete = () => isSuperAdmin();

  const canPromoteToOwner = () => false;
  const canDeleteOwner = () => false;
  const canLockOwner = () => false;

  // ---------- API logique hiérarchique ----------
  const canModifyUser = (targetUserId, targetUserIsOwner = false, targetUserRole = "vendeuse") => {
    if (!canManageUsers()) return false;
    if (!targetUserId || targetUserId === user?.uid) return false;

    if (isSuperAdmin()) return !targetUserIsOwner;

    const t = safeLower(targetUserRole);
    if (t === "admin" || t === "docteur") return false;
    if (targetUserIsOwner) return false;

    return true;
  };

  const canChangeUserRole = (targetUserId, targetUserIsOwner = false, currentRole, newRole) => {
    if (!canModifyUser(targetUserId, targetUserIsOwner, currentRole)) return false;
    const rNew = safeLower(newRole);
    if (!["docteur", "admin", "vendeuse", "assistant"].includes(rNew)) return false;
    if ((rNew === "admin" || rNew === "docteur") && !isSuperAdmin()) return false;
    return true;
  };

  const canPromoteToDoctor = (targetUserId, targetUserIsOwner = false, currentRole) => {
    if (!isSuperAdmin()) return false;
    return canModifyUser(targetUserId, targetUserIsOwner, currentRole) &&
           ["vendeuse", "assistant"].includes(safeLower(currentRole));
  };

  const canDemoteToVendeuse = (targetUserId, targetUserIsOwner = false, currentRole) => {
    if (!canModifyUser(targetUserId, targetUserIsOwner, currentRole)) return false;
    const t = safeLower(currentRole);
    if (targetUserIsOwner) return false;
    return t === "admin" || t === "docteur";
  };

  // ---------- Introspection ----------
  const can = (permission) => {
    if (!canAccessApp()) return false;

    const ownerOnly = new Set([
      "modifier_roles",
      "promouvoir_utilisateur",
      "retrograder_utilisateur",
    ]);
    if (ownerOnly.has(permission)) return isSuperAdmin();

    // Gestion des utilisateurs : accessible au propriétaire, admin et pharmacien (docteur)
    if (permission === "gerer_utilisateurs" || permission === "voir_gestion_utilisateurs") {
      const r = safeLower(role);
      return isOwner || r === "admin" || r === "docteur";
    }

    if (isOwner) return true;

    if (DOCTOR_ONLY_PERMISSIONS.includes(permission)) {
      const r = safeLower(role);
      return r === "docteur" || r === "admin";
    }

    if (removedPermissions?.includes?.(permission)) return false;
    return true;
  };

  const getUserPermissions = () => [...effectivePermissions];
  const hasRestrictions = () => !!(removedPermissions && removedPermissions.length > 0);
  const getRemovedPermissions = () => removedPermissions || [];
  const hasFullAccess = () => {
    const r = safeLower(role);
    if (isOwner || r === "docteur" || r === "admin") return true;
    return !hasRestrictions();
  };
  const getPermissionStatus = (permission) => {
    if (DOCTOR_ONLY_PERMISSIONS.includes(permission)) return "doctor_only";
    if (!hasRestrictions()) return "allowed_full_access";
    return removedPermissions.includes(permission) ? "removed" : "allowed";
  };
  const getPermissionChanges = () => {
    const all = (permissions.docteur || []).filter(
      (p) => !DOCTOR_ONLY_PERMISSIONS.includes(p)
    );
    const removed = getRemovedPermissions();
    return {
      hasChanges: removed.length > 0,
      total: all.length,
      allowed: all.length - removed.length,
      removed: removed.length,
      removedList: removed,
    };
  };

  // Live refresh des retraits
  const refreshRemovedPermissions = async (userId = null) => {
    const target = userId || user?.uid;
    if (!target) return;
    try {
      const snap = await _getDoc(_doc(_db, "users", target));
      if (snap.exists()) {
        const d = snap.data();
        setRemovedPermissions(normalizePermissions(d.removedPermissions || []));
      }
    } catch (e) {
      logger.error("[UserRoleContext] Erreur rechargement permissions:", e);
    }
  };
  const refreshCustomPermissions = refreshRemovedPermissions;

  // --------- RÉTRO-COMPAT: helpers attendus ailleurs ----------
  const hasCustomPermissions = () => hasRestrictions(); // fonction
  const getExtraPermissions = () => [];                 // placeholder

  // ---------- Messages et stats ----------
  const getBlockMessage = () => {
    if (!user || !authReady) return "Connexion en cours...";
    if (isDeleted) return "Ce compte a été supprimé par l'administrateur";
    if (isLocked && !isOwner) return "Compte temporairement verrouillé";
    if (!isActive && !isOwner) return "Compte désactivé par l'administrateur";
    return null;
  };

  const getUserStats = () => {
    const changes = getPermissionChanges();
    return {
      isConnected: !!user && authReady,
      isActive,
      isLocked,
      isDeleted,
      isOwner,
      role,
      societeId,
      hasAccess: canAccessApp(),
      blockReason: getBlockMessage(),
      authReady,
      removedPermissions: getRemovedPermissions(),
      hasRestrictions: hasRestrictions(),
      hasFullAccess: hasFullAccess(),
      permissionChanges: changes,
      totalPermissions: getUserPermissions().length,
      privileges: {
        canManageUsers: canManageUsers(),
        canChangeRoles: canChangeRoles(),
        canDeleteSociete: canDeleteSociete(),
        isUntouchable: isOwner,
        canPromoteUsers: canChangeRoles(),
        canDemoteUsers: canChangeRoles(),
      },
    };
  };

  const getPermissionMessages = () => {
    const msgs = [];
    if (!authReady) {
      msgs.push({ type: "info", text: "Vérification des permissions en cours..." });
      return msgs;
    }
    if (isOwner) msgs.push({ type: "success", text: "Vous êtes le propriétaire de cette pharmacie" });
    if (isDeleted) msgs.push({ type: "error", text: "Ce compte a été supprimé par l'administrateur" });
    else if (isLocked && !isOwner) msgs.push({ type: "warning", text: "Votre compte est temporairement verrouillé" });
    else if (!isActive && !isOwner) msgs.push({ type: "warning", text: "Votre compte est désactivé" });

    if (hasRestrictions()) {
      const changes = getPermissionChanges();
      msgs.push({ type: "warning", text: `${changes.removed} permission(s) ont été retirées` });
    } else if (safeLower(role) === "vendeuse" || safeLower(role) === "assistant") {
      msgs.push({ type: "success", text: "Vous avez un accès complet (aucun retrait)" });
    }

    if (adminPopup) msgs.push({ type: "info", text: adminPopup });
    if (paymentWarning) msgs.push({ type: "warning", text: paymentWarning });
    return msgs;
  };

  const getUserRoleDisplay = () => {
    const r = safeLower(role);
    let base = "Utilisateur";
    if (r === "docteur") base = "Pharmacien";
    if (r === "admin") base = "Administrateur";
    if (r === "vendeuse") base = "Vendeuse";
    if (r === "assistant") base = "Assistant(e)";
    if (isOwner) base += " (Propriétaire)";
    if (hasRestrictions() && (r === "vendeuse" || r === "assistant")) {
      const ch = getPermissionChanges();
      base += ` (-${ch.removed})`;
    } else if (r === "vendeuse" || r === "assistant") {
      base += " (Accès complet)";
    }
    return base;
  };

  const getOwnershipStatus = () => {
    if (!user) return "Non connecté";
    if (isOwner) return "Propriétaire";
    const r = safeLower(role);
    if (r === "docteur") return "Pharmacien";
    if (r === "admin") return "Administrateur";
    return "Utilisateur standard";
  };

  const getCurrentRole = () => currentRoleRef.current || role;
  const getCurrentUser = () => currentUserRef.current || user;

  /**
   * refreshSession — force le rechargement du profil Firestore depuis le serveur.
   * Lit directement Firestore (ne dépend pas de onAuthStateChanged).
   * Utile si l'utilisateur signale une perte de données sans déconnexion.
   */
  // Mettre à jour la ref dès que refreshSession est (re)définie
  // eslint-disable-next-line react-hooks/exhaustive-deps
  const refreshSession = async () => {
    if (refreshingRef.current) return;
    refreshingRef.current = true;
    const authInst = getAuth();
    const cu = authInst.currentUser;
    if (!cu) { refreshingRef.current = false; return; }

    setLoading(true);
    const lsKey = `pharma_profile_${cu.uid}`;
    const mySessionId = ++sessionIdRef.current;

    // Détacher les anciens listeners pour repartir proprement
    if (lastUserKeyRef.current) { detachKey(lastUserKeyRef.current); lastUserKeyRef.current = null; }
    if (lastSocKeyRef.current)  { detachKey(lastSocKeyRef.current);  lastSocKeyRef.current  = null; }
    currentUserIdRef.current  = cu.uid;
    currentUserRef.current    = null;
    currentSocieteIdRef.current = null;

    await ensureFirestore();
    try {
      const userRef = _doc(_db, "users", cu.uid);
      const userKey = `users/${cu.uid}`;
      lastUserKeyRef.current = userKey;

      attachUniqueSnapshot(
        userKey,
        userRef,
        (snap) => {
          if (sessionIdRef.current !== mySessionId) return;
          refreshingRef.current = false;
          let base;
          if (snap.exists()) {
            const data = snap.data() || {};
            const userRole = (typeof data.role === "string" ? data.role : "vendeuse").toLowerCase();
            base = {
              uid: cu.uid,
              email: cu.email || "",
              displayName: data.displayName || data.nom || cu.displayName || "",
              photoURL: data.photoURL || cu.photoURL || "",
              nom: data.nom || data.displayName || cu.displayName || "",
              role: userRole,
              societeId: data.societeId || null,
              isOwner: data.isOwner === true,
              active: data.active !== false && data.isActive !== false,
              locked: data.locked === true || data.isLocked === true,
              deleted: data.deleted === true,
              adminPopup: data.adminPopup || null,
              paymentWarning: data.paymentWarning || null,
              removedPermissions: normalizePermissions(data.removedPermissions || []),
            };
            if (base.societeId) {
              try { localStorage.setItem(lsKey, JSON.stringify(base)); } catch {}
            }
          } else {
            try {
              const cached = JSON.parse(localStorage.getItem(lsKey) || "null");
              if (cached?.societeId) base = cached;
            } catch {}
            if (!base) base = { uid: cu.uid, email: cu.email || "", role: "docteur", societeId: null, isOwner: false, active: true, locked: false, deleted: false, adminPopup: null, paymentWarning: null, removedPermissions: [] };
          }
          currentUserRef.current  = base;
          currentRoleRef.current  = base.role;
          setUser(base);
          setRole(base.role);
          setSocieteId(base.societeId);
          setIsOwner(!!base.isOwner);
          setIsActive(base.active !== false);
          setIsLocked(!!base.locked);
          setIsDeleted(!!base.deleted);
          setAdminPopup(base.adminPopup || null);
          setPaymentWarning(base.paymentWarning || null);
          setRemovedPermissions(normalizePermissions(base.removedPermissions || []));
          setLoading(false);
        },
        async (err) => {
          if (sessionIdRef.current !== mySessionId) return;
          refreshingRef.current = false;
          const errMsg = String(err?.message || "");
          const isPermErr = errMsg.includes("Missing or insufficient permissions") ||
                            errMsg.includes("PERMISSION_DENIED");

          if (isPermErr) {
            // Permission-denied dans refreshSession : token probablement expiré.
            // Forcer le refresh du token Firebase et incrémenter le compteur
            // pour ne pas boucler indéfiniment.
            const authInst = getAuth();
            if (authInst.currentUser && recoveryAttemptsRef.current < 3) {
              try {
                await authInst.currentUser.getIdToken(true);
                await new Promise(r => setTimeout(r, 2000));
              } catch (_) {}
            } else {
              // Trop de tentatives ou pas d'utilisateur → afficher le bouton
              setSessionError(true);
            }
            setLoading(false);
            return;
          }

          logger.warn("[refreshSession] Erreur snapshot:", err);
          // Fallback localStorage
          try {
            const cached = JSON.parse(localStorage.getItem(lsKey) || "null");
            if (cached?.societeId) {
              currentUserRef.current = cached;
              currentRoleRef.current = cached.role;
              setUser(cached); setRole(cached.role); setSocieteId(cached.societeId);
              setIsOwner(!!cached.isOwner); setIsActive(cached.active !== false);
              setIsLocked(!!cached.locked); setIsDeleted(!!cached.deleted);
              setRemovedPermissions(normalizePermissions(cached.removedPermissions || []));
            }
          } catch {}
          setLoading(false);
          await tryRecoverWatchError(err);
        }
      );
    } catch (e) {
      refreshingRef.current = false;
      logger.error("[refreshSession] Erreur:", e);
      setLoading(false);
    }
  };

  // Garder refreshSessionRef à jour
  refreshSessionRef.current = refreshSession;

  /**
   * restoreOfflineSession — restaure une session depuis le cache localStorage quand hors ligne.
   * Permet à l'utilisateur d'accéder à l'app sans connexion Firebase valide.
   * Quand Firebase revient avec un utilisateur valide, la session normale reprend automatiquement.
   */
  const restoreOfflineSession = (profile) => {
    if (!profile?.uid) return;
    offlineModeRef.current = true;
    setIsOfflineSession(true);
    currentUserRef.current = profile;
    // Persister dans sessionStorage → survit à window.location.reload()
    try { sessionStorage.setItem('pharma_offline_uid', profile.uid); } catch {}
    const normalized = normalizePermissions(profile.removedPermissions || []);
    setUser(profile);
    setRole(profile.role);
    setSocieteId(profile.societeId || null);
    setIsOwner(!!profile.isOwner);
    setIsActive(profile.active !== false);
    setIsLocked(!!profile.locked);
    setIsDeleted(!!profile.deleted);
    setAdminPopup(profile.adminPopup || null);
    setPaymentWarning(profile.paymentWarning || null);
    setRemovedPermissions(normalized);
    setLoading(false);
  };

  // Reset du compteur quand societeId est obtenu avec succès
  useEffect(() => {
    if (societeId) recoveryAttemptsRef.current = 0;
  }, [societeId]);

  // =====================================================
  // Auto-recovery : user connecté mais societeId null
  // Limite à 3 tentatives avec backoff exponentiel
  // (2.5s → 5s → 10s) pour éviter la boucle infinie
  // =====================================================
  useEffect(() => {
    if (loading) return;
    if (!user) return;
    if (societeId) { setSessionError(false); return; } // session OK → reset
    if (refreshingRef.current) return;
    if (recoveryAttemptsRef.current >= 3) {
      setSessionError(true); // toutes tentatives épuisées → signaler l'erreur
      return;
    }

    const attempt = recoveryAttemptsRef.current;
    const delay = 2500 * Math.pow(2, attempt); // 2.5s, 5s, 10s
    const timer = setTimeout(() => {
      if (!societeId && !refreshingRef.current) {
        recoveryAttemptsRef.current += 1;
        if (recoveryAttemptsRef.current > 3) {
          // Toutes tentatives épuisées → afficher le bouton de réinitialisation
          setSessionError(true);
          return;
        }
        logger.warn(`[UserRoleContext] societeId manquant — auto-recovery… (tentative ${recoveryAttemptsRef.current}/3)`);
        refreshSessionRef.current?.();
        // Après la 3ème tentative, programmer le setSessionError si ça échoue encore
        if (recoveryAttemptsRef.current >= 3) {
          setTimeout(() => {
            if (!currentSocieteIdRef.current) {
              setSessionError(true);
            }
          }, 12000); // 12s de grâce après la dernière tentative
        }
      }
    }, delay);
    return () => clearTimeout(timer);
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [loading, user, societeId]);

  const contextValue = useMemo(() => ({
    // États principaux
    role,
    user,
    societeId,
    societeName,
    loading,
    authReady,

    // Statuts
    isOwner,
    isActive,
    isLocked,
    isDeleted,
    isSocieteLocked,
    adminPopup,
    paymentWarning,
    sessionError,
    refreshSession,
    isOfflineSession,

    // Solde & Avances
    montantDu,
    totalAvances,
    soldeDu,

    // Permissions (logique inversée)
    removedPermissions,
    hasRestrictions,
    getRemovedPermissions,
    hasFullAccess,
    getPermissionStatus,
    getPermissionChanges,
    getUserPermissions,
    refreshRemovedPermissions,
    refreshCustomPermissions,
    can,
    canAccessApp,

    // === RÉTRO-COMPAT ===
    hasCustomPermissions,
    hasCustomPermissionsFlag: hasRestrictions(),
    getExtraPermissions,

    // Rôles / hiérarchie
    isAdmin,
    isSuperAdmin,
    canManageUsers,
    canChangeRoles,
    canDeleteSociete,
    canPromoteToOwner,
    canDeleteOwner,
    canLockOwner,
    canModifyUser,
    canChangeUserRole,
    canPromoteToDoctor,
    canDemoteToVendeuse,

    // Infos UI
    getBlockMessage,
    getUserStats,
    getPermissionMessages,
    getUserRoleDisplay,
    getOwnershipStatus,

    // Getters courants
    getCurrentRole,
    getCurrentUser,

    // Récupération manuelle de session
    restoreOfflineSession,
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }), [
    role, user, societeId, societeName, loading, authReady,
    isOwner, isActive, isLocked, isDeleted, isSocieteLocked, adminPopup, paymentWarning,
    sessionError, isOfflineSession,
    montantDu, totalAvances, soldeDu,
    removedPermissions, effectivePermissions,
  ]);

  return (
    <UserRoleContext.Provider value={contextValue}>
      {children}
    </UserRoleContext.Provider>
  );
}
